import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sodraApi, type SodraDeclarationDto, type SodraGenerateParams } from '../../api/sodraDeclarations';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate } from '../../utils/formatDate';

/**
 * Sodros deklaracijos — SAM (mėnesinė), 1-SD (priėmimai), 2-SD (atleidimai).
 * Generate → review the per-person rows → save (Draft) → approve/lock → CSV
 * for the Sodra portal. The declaration reads the wage-sheet and worker-event
 * mirrors — read-only, both planes.
 */
type DeclType = 'SAM' | 'SD1' | 'SD2' | 'SD12';

const STATUS_CHIP: Record<string, string> = {
  Draft: 'bg-gray-100 text-gray-600',
  UnderReview: 'bg-blue-50 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Locked: 'bg-[#071E3B] text-white',
};

export default function SodraDeclarations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [type, setType] = useState<DeclType>('SAM');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [from, setFrom] = useState(`${now.getFullYear()}-01-01`);
  const [to, setTo] = useState(`${now.getFullYear()}-12-31`);
  const [preview, setPreview] = useState<SodraDeclarationDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params: SodraGenerateParams = type === 'SAM'
    ? { type: 'SAM', year, month }
    : type === 'SD1'
      ? { type: 'SD1', from, to }
      : type === 'SD12'
        ? { type: 'SD12', from, to }
        : { type: 'SD2', from, to, accrualYear: year, accrualMonth: month };

  const { data: saved } = useQuery({
    queryKey: ['sodra-declarations'],
    queryFn: () => sodraApi.list(),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['sodra-declarations'] });

  const generateMutation = useMutation({
    mutationFn: () => sodraApi.generate(params),
    onSuccess: (dto) => { setError(null); setPreview(dto); },
    onError: (err: Error) => setError(err.message),
  });

  const saveMutation = useMutation({
    mutationFn: () => sodraApi.save(params),
    onSuccess: async () => {
      setPreview(null);
      setError(null);
      await refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const actionMutation = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'submit' | 'approve' | 'lock' | 'reject' }) =>
      sodraApi.action(id, action),
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });

  const exportCsv = (id: number, fileName: string) => {
    sodraApi.exportCsv(id, fileName).catch(err => setError(err instanceof Error ? err.message : String(err)));
  };

  const periodLabel = type === 'SAM'
    ? `${year}-${String(month).padStart(2, '0')}`
    : `${from}…${to}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('sodra.title')}</h1>
          <p className="text-sm text-gray-500">{t('sodra.subtitle')}</p>
        </div>
        <button
          className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          onClick={() => navigate('/payroll')}
        >
          {t('common.back')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}

      {/* Generator */}
      <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 space-y-3">
        <div className="flex flex-wrap gap-2">
          {(['SAM', 'SD1', 'SD2', 'SD12'] as DeclType[]).map(tp => (
            <button
              key={tp}
              onClick={() => { setType(tp); setPreview(null); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${
                type === tp
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'text-gray-600 bg-white border-gray-300 hover:border-gray-500'
              }`}
            >
              {t(`sodra.type.${tp}`)}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {type === 'SAM' ? (
            <>
              <label className="text-xs text-gray-500">
                {t('sodra.year')}
                <input type="number" min={2000} max={2100} value={year}
                  onChange={e => setYear(Number(e.target.value) || year)}
                  className="ml-2 w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </label>
              <label className="text-xs text-gray-500">
                {t('sodra.month')}
                <select value={month} onChange={e => setMonth(Number(e.target.value))}
                  className="ml-2 px-2 py-1.5 text-sm border border-gray-300 rounded-md">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            <>
              <label className="text-xs text-gray-500">
                {t('sodra.from')}
                <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                  className="ml-2 px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </label>
              <label className="text-xs text-gray-500">
                {t('sodra.to')}
                <input type="date" value={to} onChange={e => setTo(e.target.value)}
                  className="ml-2 px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </label>
              {type === 'SD2' && (
                <span className="text-xs text-gray-400">{t('sodra.sd2AccrualHint', { year, month })}</span>
              )}
            </>
          )}
          <div className="flex-1" />
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-40"
          >
            {generateMutation.isPending ? '…' : t('sodra.generate')}
          </button>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div className="bg-white border border-primary-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              {t(`sodra.type.${preview.declarationType}`)} · {periodLabel} · {preview.personCount} {t('sodra.persons')}
            </h3>
            <div className="flex gap-2">
              {type === 'SAM' && (
                <span className="text-sm text-gray-600">
                  {t('sodra.income')}: <span className="font-semibold num">{formatMoney(preview.totalIncome)}</span>
                  {' · '}
                  {t('sodra.payments')}: <span className="font-semibold num">{formatMoney(preview.totalPayments)}</span>
                </span>
              )}
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
              >
                {saveMutation.isPending ? '…' : t('sodra.save')}
              </button>
            </div>
          </div>

          {preview.warnings.length > 0 && (
            <ul className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-xs text-amber-800 list-disc list-inside">
              {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}

          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                  <th className="py-1.5 pr-2 w-8">#</th>
                  <th className="py-1.5 pr-2">{t('sodra.col.code')}</th>
                  <th className="py-1.5 pr-2">{t('sodra.col.name')}</th>
                  {type !== 'SD1' && <th className="py-1.5 pr-2 text-right">{t('sodra.col.income')}</th>}
                  {type !== 'SD1' && <th className="py-1.5 pr-2 text-right">{t('sodra.col.payment')}</th>}
                  {type !== 'SD1' && <th className="py-1.5 pr-2 text-right">{t('sodra.col.rate')}</th>}
                  {type === 'SD1' && <th className="py-1.5 pr-2">{t('sodra.col.startDate')}</th>}
                  {type === 'SD1' && <th className="py-1.5 pr-2">{t('sodra.col.subtype')}</th>}
                  {type === 'SD1' && <th className="py-1.5 pr-2">LPK</th>}
                  {type === 'SD2' && <th className="py-1.5 pr-2">{t('sodra.col.endDate')}</th>}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map(r => (
                  <tr key={r.rowNumber} className="border-b border-gray-50">
                    <td className="py-1.5 pr-2 text-gray-400">{r.rowNumber}</td>
                    <td className="py-1.5 pr-2 font-mono text-xs">{r.personCode || '—'}</td>
                    <td className="py-1.5 pr-2 text-gray-800">{r.personName}</td>
                    {type !== 'SD1' && <td className="py-1.5 pr-2 text-right num">{formatMoney(r.income)}</td>}
                    {type !== 'SD1' && <td className="py-1.5 pr-2 text-right num">{formatMoney(r.payment)}</td>}
                    {type !== 'SD1' && <td className="py-1.5 pr-2 text-right num text-gray-500">{r.taxRate ?? '—'}</td>}
                    {type === 'SD1' && <td className="py-1.5 pr-2">{r.extras?.startDate && formatDate(r.extras.startDate)}</td>}
                    {type === 'SD1' && <td className="py-1.5 pr-2 text-xs text-gray-600">{r.extras?.subtypeText ?? ''}</td>}
                    {type === 'SD1' && <td className="py-1.5 pr-2 font-mono text-xs">{r.extras?.lpk ?? '—'}</td>}
                    {type === 'SD2' && <td className="py-1.5 pr-2">{r.extras?.endDate && formatDate(r.extras.endDate)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Saved declarations */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <th className="px-3 py-2 font-semibold">{t('sodra.col.form')}</th>
              <th className="px-3 py-2 font-semibold">{t('sodra.col.period')}</th>
              <th className="px-3 py-2 font-semibold">{t('sodra.col.status')}</th>
              <th className="px-3 py-2 font-semibold text-right">{t('sodra.col.people')}</th>
              <th className="px-3 py-2 font-semibold text-right">{t('sodra.col.income')}</th>
              <th className="px-3 py-2 font-semibold text-right">{t('sodra.col.payment')}</th>
              <th className="px-3 py-2 font-semibold">{t('findings.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {(saved ?? []).map(h => (
              <tr key={h.id} className="border-t border-gray-50">
                <td className="px-3 py-2 font-medium text-gray-900">{h.formCode}</td>
                <td className="px-3 py-2 text-gray-600">
                  {h.declarationType === 'SAM'
                    ? `${h.year}-${String(h.month ?? 1).padStart(2, '0')}`
                    : h.dateFrom && h.dateTo ? `${h.dateFrom.slice(0, 10)}…${h.dateTo.slice(0, 10)}` : '—'}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${STATUS_CHIP[h.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {t(`sodra.status.${h.status}`)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right num">{h.personCount}</td>
                <td className="px-3 py-2 text-right num">{formatMoney(h.totalIncome)}</td>
                <td className="px-3 py-2 text-right num">{formatMoney(h.totalPayments)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {h.status === 'Draft' && (
                      <button onClick={() => actionMutation.mutate({ id: h.id, action: 'approve' })}
                        className="px-2.5 py-1 text-xs font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:border-primary-400">
                        {t('sodra.approve')}
                      </button>
                    )}
                    {h.status === 'Approved' && (
                      <button onClick={() => actionMutation.mutate({ id: h.id, action: 'lock' })}
                        className="px-2.5 py-1 text-xs font-medium text-[#071E3B] bg-gray-100 border border-gray-300 rounded-lg hover:border-gray-500">
                        {t('sodra.lock')}
                      </button>
                    )}
                    <button onClick={() => exportCsv(h.id, `${h.formCode.replace(/[^A-Za-z0-9.-]/g, '_')}.csv`)}
                      className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500">
                      CSV
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {(!saved || saved.length === 0) && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">{t('sodra.empty')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
